import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy } from "lucide-react";
import { api } from "../lib/api";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Which counties are actually filing. All 47, zeros visible, because the zeros are the point. */
export const CountyLeaderboard = ({ limit = 0, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/apply/leaderboard").then((r) => setData(r.data)).catch(() => setData(null));
  }, [refreshKey]);

  if (!data) return null;
  const rows = showAll || !limit ? data.rows : data.rows.slice(0, limit);

  return (
    <section className="mt-20" data-testid="county-leaderboard">
      <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-ochre">County leaderboard</p>
      <h2 className="mt-4 text-base font-semibold text-white md:text-lg">{data.heading}</h2>
      <p className="body-copy mt-4 max-w-3xl">{data.intro}</p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="leaderboard-stats">
        {[
          ["Proposals filed", data.proposals_total],
          ["Counties moving", `${data.counties_with_proposals} of ${data.counties_total}`],
          ["Counties still silent", data.counties_silent],
          ["Leading county", data.leader || "Nobody yet"],
        ].map(([k, v], i) => (
          <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`leaderboard-stat-${i}`}>
            <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</p>
            <p className="mono mt-3 text-base text-white md:text-lg">{v}</p>
          </div>
        ))}
      </div>

      {data.proposals_total === 0 ? (
        <p className="mono mt-8 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-6 text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-ochre" data-testid="leaderboard-empty">
          {data.empty_note}
        </p>
      ) : null}

      <div className="no-scrollbar mt-8 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[620px] text-left" data-testid="leaderboard-table">
          <thead>
            <tr className="border-b border-white/15 bg-[#0b0b0b]">
              {["#", "County", "Applications", "Referrals", "Total", "Last filed"].map((h) => (
                <th key={h} className="mono px-4 py-3 text-[0.52rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.county} className={`border-b border-white/5 ${r.total ? "" : "opacity-80"}`} data-testid={`leaderboard-row-${r.county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <td className="mono px-4 py-3 text-sm text-zinc-400">{r.rank}</td>
                <td className="px-4 py-3 text-sm text-white">
                  {r.rank === 1 && r.total ? <Trophy size={12} className="mr-2 inline text-ochre" /> : null}
                  {r.county}
                </td>
                <td className="mono px-4 py-3 text-sm text-ochre">{r.applications}</td>
                <td className="mono px-4 py-3 text-sm text-zinc-300">{r.referrals}</td>
                <td className="mono px-4 py-3 text-sm text-white">{r.total}</td>
                <td className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">
                  {r.total ? day(r.last_filed) : "Nothing yet"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {limit && data.rows.length > limit ? (
          <button onClick={() => setShowAll((s) => !s)} className="btn-ghost" data-testid="leaderboard-toggle">
            {showAll ? "Show the top counties only" : `Show all ${data.counties_total} counties`}
          </button>
        ) : null}
        <Link to="/apply" className="btn-primary inline-flex items-center gap-2" data-testid="leaderboard-apply">
          Put your county on the board <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
};

export default CountyLeaderboard;
